import { useDeferredValue, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CategoryRef } from '../../shared/api';
import { BookCover } from '../components/BookCover';
import { CategoryChips } from '../components/CategoryChips';
import { useResource } from '../hooks/useResource';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatAuthors } from '../lib/format';
import { matchesQuery } from '../lib/search';

/** A friend's collection, read-only. */
export function FriendCollectionPage() {
  const { id = '' } = useParams();
  const friendId = Number(id);
  const { data, error, loading, reload } = useResource(`friend:${id}`, (signal) => api.friendCollection(friendId, signal));
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const deferredQuery = useDeferredValue(query.trim());

  // Only the categories their books are actually in, in the order the server lists them.
  const categories = useMemo(() => {
    const seen = new Map<number, CategoryRef & { count: number }>();
    for (const book of data?.books ?? []) {
      for (const category of book.categories) {
        const entry = seen.get(category.id) ?? { ...category, count: 0 };
        entry.count += 1;
        seen.set(category.id, entry);
      }
    }
    return [...seen.values()];
  }, [data]);

  const visible = useMemo(
    () =>
      (data?.books ?? []).filter(
        (book) =>
          (categoryFilter === 'all' || book.categories.some((category) => String(category.id) === categoryFilter)) &&
          matchesQuery(book, deferredQuery),
      ),
    [data, categoryFilter, deferredQuery],
  );

  const backLink = (
    <Link className="back-link" to="/friends">
      ← Friends
    </Link>
  );

  if (!data) {
    if (loading) return <p className="page-status">Loading collection…</p>;
    const notFriend = error instanceof ApiError && (error.status === 404 || error.status === 400);
    return (
      <section className="page-status" role="alert">
        {backLink}
        <p className={notFriend ? undefined : 'error-text'}>
          {notFriend ? "That person isn't one of your friends." : errorMessage(error)}
        </p>
        {!notFriend && (
          <button type="button" className="btn btn--primary" onClick={reload}>
            Try again
          </button>
        )}
      </section>
    );
  }

  const { friend, books } = data;
  const name = friend.name ?? friend.email;

  return (
    <section className="collection-page">
      {backLink}
      <header className="page-header">
        <div>
          <h1>{name}'s collection</h1>
          <p className="muted">
            {books.length} {books.length === 1 ? 'book' : 'books'}
          </p>
        </div>
        <Link className="btn btn--ghost btn--small" to={`/friends/${id}/bar`}>
          Their bar
        </Link>
      </header>

      {books.length === 0 ? (
        <div className="empty-state">
          <p>{name} hasn't added any books yet.</p>
        </div>
      ) : (
        <>
          <div className="filters">
            <input
              type="search"
              className="filters__search"
              placeholder="Search title, author or ISBN"
              aria-label={`Search ${name}'s collection`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {categories.length > 0 && (
              <select
                className="filters__status"
                aria-label="Filter by category"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="all">All categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={String(category.id)}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            )}
          </div>

          {visible.length === 0 ? (
            <p className="hint">No books match.</p>
          ) : (
            <ul className="book-grid">
              {visible.map((book) => (
                <li key={book.isbn}>
                  <div className="book-tile book-tile--static">
                    <BookCover src={book.coverUrl} title={book.title} size="small" />
                    <div className="book-tile__body">
                      <h2 className="book-tile__title">{book.title ?? `ISBN ${book.isbn}`}</h2>
                      {book.status === 'complete' && <p className="book-tile__authors">{formatAuthors(book.authors)}</p>}
                      <CategoryChips categories={book.categories} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
